/**
 * Utility functions for role-based access control and UI rendering.
 * Role comparisons use selectedRole (the role the user is currently acting as)
 * and are case-insensitive. 'Process Compliance Officer' is treated as the
 * employee tier for all UI-level checks.
 */

export interface RoleUtilsProps {
  userRoles: string[];
  selectedRole: string;
}

export const createRoleUtils = ({ userRoles, selectedRole }: RoleUtilsProps) => {
  const roleLower = selectedRole.toLowerCase();

  const hasImportAccess = () => {
    return roleLower === "hr" || roleLower === "admin";
  };

  const isEmployee = () => {
    return roleLower === "employee" || roleLower === "process compliance officer";
  };

  const isManager = () => {
    return roleLower === "manager";
  };

  const isHROrAdmin = () => {
    return roleLower === "hr" || roleLower === "admin";
  };

  const getPageTitle = () => {
    if (isHROrAdmin()) {
      return "Attendance Records - Organization View";
    } else if (isManager()) {
      return "Attendance Records - Team View";
    } else {
      return "Attendance Records - My View";
    }
  };

  const getRoleDescription = () => {
    if (isEmployee()) {
      return "View your personal attendance records, track your working hours from last Monday, and monitor your Work From Home days for the current month.";
    } else if (isManager()) {
      return "View attendance records for your assigned team members, monitor team statistics, and track overall team performance metrics.";
    } else if (isHROrAdmin()) {
      return "Access organization-wide attendance data, import bulk attendance records via CSV/Excel, and generate comprehensive reports for all employees.";
    }
    return "";
  };

  return {
    hasImportAccess,
    isEmployee,
    isManager,
    isHROrAdmin,
    getPageTitle,
    getRoleDescription,
  };
};

export default createRoleUtils;
